import React, { useState, useEffect, createRef } from 'react';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { FormInput, FormTextArea, FormCheckbox, FormSwitch } from '../FormComponents';
import { requiredValidator } from '../Validators';
import {
  FormDetail,
  FormSection,
  FormQuestion,
  FormQuestionOption,
  getFormById,
  getFormQuestionTypes,
  createFormSection,
  updateFormSection,
  createFormQuestion,
  updateFormQuestion,
  updateForm,
  deleteForm,
  deleteFormSection,
  deleteFormQuestion,
  DropdownOption,
  getFormDocuments,
} from '../../../data/formData';
import { deleteDocument } from '../../../data/fileData';
import { fileDocumentTypes } from '../../../data/interfaces';
import { useToast } from '../../../contexts/ToastContext';
import {
  FaPlus,
  FaEdit,
  FaGripVertical,
  FaTrash,
  FaSave,
  FaTimes,
  FaFilePdf,
  FaFileCsv,
  FaFileAlt,
  FaFileImage,
} from 'react-icons/fa';
import {
  Upload,
  UploadOnStatusChangeEvent,
  UploadFileInfo,
  UploadListItemProps,
  ExternalDropZone,
} from '@progress/kendo-react-upload';
import {
  TabStrip,
  TabStripTab,
} from '@progress/kendo-react-layout';
import QuoteDocumentRow from '../../QuoteDocumentRow';
import './FormsStyles.css';

const apiURL = process.env.REACT_APP_API_URL;

interface EditFormDetailsProps {
  formId: number;
  onUpdate: () => void;
  onClose: () => void;
  // False for view-only access: the form is shown but nothing can be changed.
  canEdit?: boolean;
}

const EditFormDetails: React.FC<EditFormDetailsProps> = ({ formId, onUpdate, onClose, canEdit = false }) => {
  const [formData, setFormData] = useState<FormDetail | null>(null);
  const [questionTypes, setQuestionTypes] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState<number | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<number | null>(null);
  const [addingSectionToForm, setAddingSectionToForm] = useState(false);
  const [addingQuestionToSection, setAddingQuestionToSection] = useState<number | null>(null);
  const [draggedSection, setDraggedSection] = useState<number | null>(null);
  const [draggedQuestion, setDraggedQuestion] = useState<{ sectionId: number; questionId: number } | null>(null);
  const [editingFormProperties, setEditingFormProperties] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [deleteSectionDialogVisible, setDeleteSectionDialogVisible] = useState(false);
  const [deleteQuestionDialogVisible, setDeleteQuestionDialogVisible] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<number | null>(null);
  const [questionToDelete, setQuestionToDelete] = useState<number | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [documentData, setDocumentData] = useState<fileDocumentTypes[]>([]);
  const [files, setFiles] = useState<UploadFileInfo[]>([]);
  const uploadRef = createRef<Upload>();
  const token = localStorage.getItem('authToken');
  const { showToast } = useToast();

  useEffect(() => {
    loadFormData();
    loadQuestionTypes();
    fetchDocumentData();
  }, [formId]);

  const fetchDocumentData = async () => {
    try {
      const data = await getFormDocuments(formId);
      setDocumentData(data);
    } catch (error: any) {
      console.error('Error loading documents:', error);
    }
  };

  const loadFormData = async () => {
    try {
      const data = await getFormById(formId);
      setFormData(data);
      setLoading(false);
    } catch (error: any) {
      console.error('Error loading form:', error);
      showToast('Error loading form details', 'error');
      setLoading(false);
    }
  };

  const loadQuestionTypes = async () => {
    try {
      const types = await getFormQuestionTypes();
      setQuestionTypes(types);
    } catch (error: any) {
      console.error('Error loading question types:', error);
    }
  };

  const handleSaveFormProperties = async (values: any) => {
    try {
      await updateForm(formId, {
        formId: formId,
        name: values.name,
        description: values.description,
        showLocationJobs: values.showLocationJobs || false,
        showLocationLinks: values.showLocationLinks || false,
        createFromTemplate: values.createFromTemplate || false,
        templateFilename: values.templateFilename || '',
        automationUrl: values.automationUrl || '',
        shortCode: values.shortCode || '',
        autoGenerateForJob: values.autoGenerateForJob || false,
      });
      showToast('Form properties updated successfully', 'success');
      setEditingFormProperties(false);
      loadFormData();
      onUpdate();
    } catch (error: any) {
      console.error('Error updating form:', error);
      showToast('Error updating form properties', 'error');
    }
  };

  const handleDeleteForm = async () => {
    try {
      await deleteForm(formId);
      showToast('Form deleted successfully', 'success');
      setDeleteDialogVisible(false);
      onClose();
      onUpdate();
    } catch (error: any) {
      console.error('Error deleting form:', error);
      showToast('Error deleting form', 'error');
    }
  };

  const handleSaveSection = async (sectionId: number | null, values: any) => {
    try {
      if (sectionId) {
        // Update existing section
        await updateFormSection(sectionId, {
          formSectionId: sectionId,
          formId: formId,
          name: values.name,
          description: values.description || '',
          order: values.order,
        });
        showToast('Section updated successfully', 'success');
      } else {
        // Create new section
        await createFormSection({
          formId: formId,
          name: values.name,
          description: values.description || '',
          order: values.order,
        });
        showToast('Section created successfully', 'success');
      }
      setEditingSection(null);
      setAddingSectionToForm(false);
      loadFormData();
      onUpdate();
    } catch (error: any) {
      console.error('Error saving section:', error);
      showToast('Error saving section', 'error');
    }
  };

  const handleSaveQuestion = async (questionId: number | null, sectionId: number, values: any) => {
    try {
      if (questionId) {
        // Update existing question
        await updateFormQuestion(questionId, {
          formQuestionId: questionId,
          formId: formId,
          name: values.name,
          description: values.description || '',
          placeholder: values.placeholder || '',
          templatePlaceholder: values.templatePlaceholder || '',
          section: sectionId,
          order: values.order,
          formQuestionTypeId: values.formQuestionTypeId,
          isRequired: values.isRequired || false,
          minLength: values.minLength || null,
          maxLength: values.maxLength || null,
          decimalPlaces: values.decimalPlaces ?? 0,
          regex: values.regex || null,
          validationMessage: values.validationMessage || '',
          options: values.options || [],
        });
        showToast('Question updated successfully', 'success');
      } else {
        // Create new question
        await createFormQuestion({
          formId: formId,
          name: values.name,
          description: values.description || '',
          placeholder: values.placeholder || '',
          templatePlaceholder: values.templatePlaceholder || '',
          section: sectionId,
          order: values.order,
          formQuestionTypeId: values.formQuestionTypeId,
          isRequired: values.isRequired || false,
          minLength: values.minLength || null,
          maxLength: values.maxLength || null,
          decimalPlaces: values.decimalPlaces ?? 0,
          regex: values.regex || null,
          validationMessage: values.validationMessage || '',
          options: values.options || [],
        });
        showToast('Question created successfully', 'success');
      }
      setEditingQuestion(null);
      setAddingQuestionToSection(null);
      loadFormData();
      onUpdate();
    } catch (error: any) {
      console.error('Error saving question:', error);
      showToast('Error saving question', 'error');
    }
  };

  // Delete handlers
  const handleDeleteSection = async () => {
    if (!sectionToDelete) return;
    
    try {
      await deleteFormSection(sectionToDelete);
      showToast('Section deleted successfully', 'success');
      setDeleteSectionDialogVisible(false);
      setSectionToDelete(null);
      loadFormData();
      onUpdate();
    } catch (error: any) {
      console.error('Error deleting section:', error);
      showToast('Error deleting section', 'error');
    }
  };

  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return;
    
    try {
      await deleteFormQuestion(questionToDelete);
      showToast('Question deleted successfully', 'success');
      setDeleteQuestionDialogVisible(false);
      setQuestionToDelete(null);
      loadFormData();
      onUpdate();
    } catch (error: any) {
      console.error('Error deleting question:', error);
      showToast('Error deleting question', 'error');
    }
  };

  // Drag and Drop handlers for sections
  const handleSectionDragStart = (e: React.DragEvent, sectionId: number) => {
    setDraggedSection(sectionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionDrop = async (e: React.DragEvent, targetSectionId: number) => {
    e.preventDefault();
    if (draggedSection === null || draggedSection === targetSectionId || !formData) return;

    const sections = [...formData.formSections];
    const draggedIndex = sections.findIndex(s => s.formSectionId === draggedSection);
    const targetIndex = sections.findIndex(s => s.formSectionId === targetSectionId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Reorder sections
    const [removed] = sections.splice(draggedIndex, 1);
    sections.splice(targetIndex, 0, removed);

    // Update order for all sections
    try {
      for (let i = 0; i < sections.length; i++) {
        await updateFormSection(sections[i].formSectionId, {
          ...sections[i],
          order: i + 1,
        });
      }
      showToast('Sections reordered successfully', 'success');
      loadFormData();
      onUpdate();
    } catch (error) {
      console.error('Error reordering sections:', error);
      showToast('Error reordering sections', 'error');
    }

    setDraggedSection(null);
  };

  // Drag and Drop handlers for questions
  const handleQuestionDragStart = (e: React.DragEvent, sectionId: number, questionId: number) => {
    setDraggedQuestion({ sectionId, questionId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleQuestionDrop = async (e: React.DragEvent, targetSectionId: number, targetQuestionId: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedQuestion || !formData) return;
    if (draggedQuestion.sectionId !== targetSectionId || draggedQuestion.questionId === targetQuestionId) {
      setDraggedQuestion(null);
      return;
    }

    const section = formData.formSections.find(s => s.formSectionId === targetSectionId);
    if (!section) return;

    const questions = [...section.formQuestions];
    const draggedIndex = questions.findIndex(q => q.formQuestionId === draggedQuestion.questionId);
    const targetIndex = questions.findIndex(q => q.formQuestionId === targetQuestionId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Reorder questions
    const [removed] = questions.splice(draggedIndex, 1);
    questions.splice(targetIndex, 0, removed);

    // Update order for all questions in this section
    try {
      for (let i = 0; i < questions.length; i++) {
        await updateFormQuestion(questions[i].formQuestionId, {
          ...questions[i],
          order: i + 1,
        });
      }
      showToast('Questions reordered successfully', 'success');
      loadFormData();
      onUpdate();
    } catch (error) {
      console.error('Error reordering questions:', error);
      showToast('Error reordering questions', 'error');
    }

    setDraggedQuestion(null);
  };

  // File upload handlers
  const icon = (fileName: string) => {
    if (fileName.includes('.pdf')) {
      return <FaFilePdf style={{ fontSize: '20pt', color: 'rgb(33, 67, 59)', marginRight: '1rem' }} />;
    } else if (fileName.includes('.csv')) {
      return <FaFileCsv style={{ fontSize: '20pt', color: 'rgb(33, 67, 59)', marginRight: '1rem' }} />;
    } else if (fileName.match(/\.(jpg|jpeg|png|gif|bmp)$/i)) {
      return <FaFileImage style={{ fontSize: '20pt', color: 'rgb(33, 67, 59)', marginRight: '1rem' }} />;
    } else {
      return <FaFileAlt style={{ fontSize: '20pt', color: 'rgb(33, 67, 59)', marginRight: '1rem' }} />;
    }
  };

  const CustomListItemUI = (props: UploadListItemProps) => {
    return (
      <ul>
        {props.files.map((file) => (
          <div style={{ fontSize: '10pt', fontFamily: 'Heebo, Sans-Serif' }} key={file.name}>
            {icon(file.name)}
            {file.name}
          </div>
        ))}
      </ul>
    );
  };

  const handleDocumentDelete = async (documentId: number) => {
    try {
      await deleteDocument(documentId);
      setDocumentData(documentData.filter((document) => document.documentId !== documentId));
      showToast('Document deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting document:', error);
      showToast('Error deleting document', 'error');
    }
  };

  const handleUploadSuccess = (event: UploadOnStatusChangeEvent) => {
    setFiles(event.newState);
    showToast('Successfully uploaded new document', 'success');
    fetchDocumentData();
  };

  if (loading || !formData) {
    return <div style={{ padding: '2rem' }}>Loading form details...</div>;
  }

  return (
    <div className="form-editor-container">
      {/* Delete Confirmation Dialog */}
      {deleteDialogVisible && (
        <Dialog title="Confirm Delete" onClose={() => setDeleteDialogVisible(false)}>
          <p style={{ margin: '25px', textAlign: 'center' }}>
            Are you sure you want to delete this form? This action cannot be undone.
          </p>
          <DialogActionsBar>
            <Button onClick={() => setDeleteDialogVisible(false)}>Cancel</Button>
            <Button themeColor="error" onClick={handleDeleteForm}>
              Delete
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Delete Section Confirmation Dialog */}
      {deleteSectionDialogVisible && (
        <Dialog title="Confirm Delete Section" onClose={() => setDeleteSectionDialogVisible(false)}>
          <p style={{ margin: '25px', textAlign: 'center' }}>
            Are you sure you want to delete this section? This action cannot be undone.
          </p>
          <DialogActionsBar>
            <Button onClick={() => setDeleteSectionDialogVisible(false)}>Cancel</Button>
            <Button themeColor="error" onClick={handleDeleteSection}>
              Delete
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Delete Question Confirmation Dialog */}
      {deleteQuestionDialogVisible && (
        <Dialog title="Confirm Delete Question" onClose={() => setDeleteQuestionDialogVisible(false)}>
          <p style={{ margin: '25px', textAlign: 'center' }}>
            Are you sure you want to delete this question? This action cannot be undone.
          </p>
          <DialogActionsBar>
            <Button onClick={() => setDeleteQuestionDialogVisible(false)}>Cancel</Button>
            <Button themeColor="error" onClick={handleDeleteQuestion}>
              Delete
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {!canEdit && <p className='k-form-hint'>You have view-only access to forms.</p>}

      <TabStrip selected={selectedTab} onSelect={(e) => setSelectedTab(e.selected)}>
        <TabStripTab title="Form Builder">
          {/* Form Properties */}
          <div className="form-properties">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>Form Properties</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {canEdit && !editingFormProperties && (
              <>
                <Button
                  size="small"
                  onClick={() => setEditingFormProperties(true)}
                >
                  <FaEdit style={{ marginRight: '0.5rem' }} />
                  Edit
                </Button>
                <Button
                  size="small"
                  themeColor="error"
                  onClick={() => setDeleteDialogVisible(true)}
                >
                  <FaTrash style={{ marginRight: '0.5rem' }} />
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>

        {canEdit && editingFormProperties ? (
          <Form
            initialValues={{
              name: formData.name,
              description: formData.description,
              showLocationJobs: formData.showLocationJobs,
              showLocationLinks: formData.showLocationLinks,
              createFromTemplate: formData.createFromTemplate || false,
              templateFilename: formData.templateFilename || '',
              automationUrl: formData.automationUrl || '',
              shortCode: formData.shortCode || '',
              autoGenerateForJob: formData.autoGenerateForJob || false,
            }}
            onSubmit={handleSaveFormProperties}
            render={(formRenderProps) => (
              <FormElement>
                <Field
                  name="name"
                  component={FormInput}
                  label="Form Name:"
                  validator={requiredValidator}
                />
                <Field
                  name="description"
                  component={FormTextArea}
                  label="Description:"
                  rows={3}
                />
                <Field
                  name="showLocationJobs"
                  component={FormCheckbox}
                  label="Show on Jobs"
                />
                <Field
                  name="showLocationLinks"
                  component={FormCheckbox}
                  label="Show on Links"
                />
                <Field
                  name="createFromTemplate"
                  component={FormSwitch}
                  label="Create from Template"
                />
                {formRenderProps.valueGetter("createFromTemplate") && (
                  <Field
                    name="templateFilename"
                    component={FormInput}
                    label="Template Filename:"
                  />
                )}
                <Field
                  name="automationUrl"
                  component={FormInput}
                  label="Automation URL:"
                  placeholder="Enter automation URL (optional)"
                />
                <Field
                  name="shortCode"
                  component={FormInput}
                  label="Short Code:"
                  placeholder="Enter short code (optional)"
                  maxLength={50}
                />
                <Field
                  name="autoGenerateForJob"
                  component={FormSwitch}
                  label="Automatically generate form and add to job"
                />
                <div className="form-actions">
                  <Button type="submit" themeColor="primary">
                    <FaSave style={{ marginRight: '0.5rem' }} />
                    Save
                  </Button>
                  <Button onClick={() => setEditingFormProperties(false)} fillMode="outline">
                    <FaTimes style={{ marginRight: '0.5rem' }} />
                    Cancel
                  </Button>
                </div>
              </FormElement>
            )}
          />
        ) : (
          <>
            <div className="form-property">
              <strong>Name:</strong> {formData.name}
            </div>
            <div className="form-property">
              <strong>Description:</strong> {formData.description}
            </div>
            <div className="form-property">
              <strong>Show on Jobs:</strong> {formData.showLocationJobs ? 'Yes' : 'No'}
            </div>
            <div className="form-property">
              <strong>Show on Links:</strong> {formData.showLocationLinks ? 'Yes' : 'No'}
            </div>
            <div className="form-property">
              <strong>Create from Template:</strong> {formData.createFromTemplate ? 'Yes' : 'No'}
            </div>
            {formData.createFromTemplate && (
              <div className="form-property">
                <strong>Template Filename:</strong> {formData.templateFilename || 'N/A'}
              </div>
            )}
            <div className="form-property">
              <strong>Automation URL:</strong> {formData.automationUrl ? (
                <a href={formData.automationUrl} target="_blank" rel="noopener noreferrer">
                  {formData.automationUrl}
                </a>
              ) : 'N/A'}
            </div>
            <div className="form-property">
              <strong>Short Code:</strong> {formData.shortCode || 'N/A'}
            </div>
            <div className="form-property">
              <strong>Automatically generate form and add to job:</strong>{' '}
              {formData.autoGenerateForJob ? 'Yes' : 'No'}
            </div>
          </>
        )}
      </div>

      {/* Sections */}
      <div className="sections-container">
        <div className="sections-header">
          <h3>Sections</h3>
          {canEdit && (
          <Button
            themeColor="primary"
            onClick={() => setAddingSectionToForm(true)}
            size="small"
          >
            <FaPlus style={{ marginRight: '0.5rem' }} />
            Add Section
          </Button>
          )}
        </div>

        {/* Add Section Form */}
        {canEdit && addingSectionToForm && (
          <SectionForm
            section={null}
            formId={formId}
            onSave={(values) => handleSaveSection(null, values)}
            onCancel={() => setAddingSectionToForm(false)}
            nextOrder={(formData.formSections.length || 0) + 1}
          />
        )}

        {formData.formSections
          .sort((a, b) => a.order - b.order)
          .map((section) => (
            <div
              key={section.formSectionId}
              className="section-card"
              draggable={canEdit}
              onDragStart={(e) => handleSectionDragStart(e, section.formSectionId)}
              onDragOver={handleSectionDragOver}
              onDrop={(e) => handleSectionDrop(e, section.formSectionId)}
            >
              <div className="section-header">
                <div className="section-drag-handle">
                  <FaGripVertical />
                </div>
                <div className="section-info">
                  <h4>
                    {section.order}. {section.name}
                  </h4>
                  {section.description && <p>{section.description}</p>}
                </div>
                {canEdit && (
                <div className="section-actions">
                  <Button
                    size="small"
                    fillMode="flat"
                    onClick={() => setEditingSection(section.formSectionId)}
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    size="small"
                    fillMode="flat"
                    onClick={() => {
                      setSectionToDelete(section.formSectionId);
                      setDeleteSectionDialogVisible(true);
                    }}
                  >
                    <FaTrash />
                  </Button>
                </div>
                )}
              </div>

              {/* Edit Section Form */}
              {canEdit && editingSection === section.formSectionId && (
                <SectionForm
                  section={section}
                  formId={formId}
                  onSave={(values) => handleSaveSection(section.formSectionId, values)}
                  onCancel={() => setEditingSection(null)}
                  nextOrder={section.order}
                />
              )}

              {/* Questions in this section */}
              <div className="questions-container">
                <div className="questions-header">
                  <strong>Questions ({section.formQuestions.length})</strong>
                  {canEdit && (
                  <Button
                    size="small"
                    fillMode="flat"
                    onClick={() => setAddingQuestionToSection(section.formSectionId)}
                  >
                    <FaPlus style={{ marginRight: '0.3rem' }} />
                    Add Question
                  </Button>
                  )}
                </div>

                {/* Add Question Form */}
                {canEdit && addingQuestionToSection === section.formSectionId && (
                  <QuestionForm
                    question={null}
                    sectionId={section.formSectionId}
                    questionTypes={questionTypes}
                    onSave={(values) => handleSaveQuestion(null, section.formSectionId, values)}
                    onCancel={() => setAddingQuestionToSection(null)}
                    nextOrder={(section.formQuestions.length || 0) + 1}
                    createFromTemplate={formData?.createFromTemplate}
                  />
                )}

                {section.formQuestions
                  .sort((a, b) => a.order - b.order)
                  .map((question) => (
                    <div
                      key={question.formQuestionId}
                      className="question-card"
                      draggable={canEdit}
                      onDragStart={(e) => handleQuestionDragStart(e, section.formSectionId, question.formQuestionId)}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDrop={(e) => handleQuestionDrop(e, section.formSectionId, question.formQuestionId)}
                    >
                      <div className="question-header">
                        <div className="question-drag-handle">
                          <FaGripVertical />
                        </div>
                        <div className="question-info">
                          <div>
                            <strong>{question.order}. {question.name}</strong>
                            {question.isRequired && <span className="required-badge">Required</span>}
                          </div>
                          <div className="question-meta">
                            Type: {question.formQuestionType.name}
                            {question.placeholder && ` • Placeholder: ${question.placeholder}`}
                          </div>
                        </div>
                        {canEdit && (
                        <div className="question-actions">
                          <Button
                            size="small"
                            fillMode="flat"
                            onClick={() => setEditingQuestion(question.formQuestionId)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            size="small"
                            fillMode="flat"
                            onClick={() => {
                              setQuestionToDelete(question.formQuestionId);
                              setDeleteQuestionDialogVisible(true);
                            }}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                        )}
                      </div>

                      {/* Edit Question Form */}
                      {canEdit && editingQuestion === question.formQuestionId && (
                        <QuestionForm
                          question={question}
                          sectionId={section.formSectionId}
                          questionTypes={questionTypes}
                          onSave={(values) => handleSaveQuestion(question.formQuestionId, section.formSectionId, values)}
                          onCancel={() => setEditingQuestion(null)}
                          nextOrder={question.order}
                          createFromTemplate={formData?.createFromTemplate}
                        />
                      )}
                    </div>
                  ))}
              </div>
            </div>
          ))}
      </div>
        </TabStripTab>

        <TabStripTab title={canEdit ? 'File Upload' : 'Files'}>
          {canEdit && (
          <>
          <ExternalDropZone
            style={{ fontSize: '10pt', fontFamily: 'Heebo, sans-serif' }}
            uploadRef={uploadRef}
          />
          <div style={{ height: '20px' }} />
          <Upload
            ref={uploadRef}
            batch={true}
            files={files}
            multiple={true}
            defaultFiles={[]}
            withCredentials={false}
            listItemUI={CustomListItemUI}
            saveHeaders={{ Authorization: `Bearer ${token}` }}
            saveUrl={token ? `https://${apiURL}/Files/Form/${formId}` : ''}
            onStatusChange={handleUploadSuccess}
          />
          </>
          )}

          <div style={{ height: '1.5rem' }} />

          <div>
            {documentData.length > 0 ? (
              <div>
                {documentData.map((document: fileDocumentTypes) => (
                  <QuoteDocumentRow
                    key={document.documentId}
                    onDocumentId={document.documentId}
                    onFileName={document.fileName}
                    onFileExtension={document.fileExtension}
                    onDocumentDelete={handleDocumentDelete}
                    appCanView={document.appCanView}
                    readOnly={!canEdit}
                  />
                ))}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '100%',
                  padding: '1rem',
                  backgroundColor: 'rgb(216 229 237)',
                  borderRadius: '0.3rem',
                  justifyContent: 'center',
                }}
              >
                No documents have been uploaded yet
              </div>
            )}
          </div>
        </TabStripTab>
      </TabStrip>
    </div>
  );
};

// Section Form Component
interface SectionFormProps {
  section: FormSection | null;
  formId: number;
  onSave: (values: any) => void;
  onCancel: () => void;
  nextOrder: number;
}

const SectionForm: React.FC<SectionFormProps> = ({ section, formId, onSave, onCancel, nextOrder }) => {
  return (
    <div className="inline-form">
      <Form
        initialValues={{
          name: section?.name || '',
          description: section?.description || '',
          order: section?.order || nextOrder,
        }}
        onSubmit={onSave}
        render={(formRenderProps) => (
          <FormElement>
            <Field
              name="name"
              component={FormInput}
              label="Section Name:"
              validator={requiredValidator}
            />
            <Field
              name="description"
              component={FormTextArea}
              label="Description:"
              rows={2}
            />
            <Field
              name="order"
              component={FormInput}
              label="Order:"
              type="number"
              validator={requiredValidator}
            />
            <div className="form-actions">
              <Button type="submit" themeColor="primary">
                <FaSave style={{ marginRight: '0.5rem' }} />
                Save
              </Button>
              <Button onClick={onCancel} fillMode="outline">
                <FaTimes style={{ marginRight: '0.5rem' }} />
                Cancel
              </Button>
            </div>
          </FormElement>
        )}
      />
    </div>
  );
};

// Question Form Component
interface QuestionFormProps {
  question: FormQuestion | null;
  sectionId: number;
  questionTypes: DropdownOption[];
  onSave: (values: any) => void;
  onCancel: () => void;
  nextOrder: number;
  createFromTemplate?: boolean;
}

// Question types whose answers are chosen from a fixed list of options, so the
// builder exposes an options editor for them. Compared case-insensitively
// against the type name. Currently only "Dropdown" is rendered as a choice list
// by the mobile app (see form_detail.dart's `case 'dropdown'`); add any further
// option-based type names here if the app grows support for them.
const OPTION_TYPE_NAMES = ['dropdown'];

// Question types that take a numeric answer, so the builder exposes the decimal
// places setting for them. Matched case-insensitively against the type name,
// the same way the mobile app's `case 'number'` picks the renderer.
const NUMERIC_TYPE_NAMES = ['number'];

// Signed on the device and stored as an image. Always required - the API
// enforces this too.
const SIGNATURE_TYPE_NAME = 'signature';

// Beyond this the stored answer stops round-tripping cleanly through the
// clients' floating point parsing. Kept in step with MaxDecimalPlaces in
// FormQuestionsModel.cs, which clamps whatever is sent.
const MAX_DECIMAL_PLACES = 6;

// The field is a plain number input, so guard against blanks and out-of-range
// entries before they reach the API.
const clampDecimalPlaces = (value: any): number => {
  const places = Math.floor(Number(value));
  if (!Number.isFinite(places) || places < 0) return 0;
  return Math.min(places, MAX_DECIMAL_PLACES);
};

const QuestionForm: React.FC<QuestionFormProps> = ({ question, sectionId, questionTypes, onSave, onCancel, nextOrder, createFromTemplate }) => {
  // Ids of the option-based question types, resolved from the loaded type list.
  const optionTypeIds = new Set(
    questionTypes
      .filter((t) => OPTION_TYPE_NAMES.includes((t.text || '').toLowerCase()))
      .map((t) => t.value)
  );

  // Ids of the numeric question types, resolved the same way.
  const numericTypeIds = new Set(
    questionTypes
      .filter((t) => NUMERIC_TYPE_NAMES.includes((t.text || '').toLowerCase()))
      .map((t) => t.value)
  );

  const signatureTypeIds = new Set(
    questionTypes
      .filter((t) => (t.text || '').toLowerCase() === SIGNATURE_TYPE_NAME)
      .map((t) => t.value)
  );

  const [options, setOptions] = useState<FormQuestionOption[]>(
    question?.formQuestionOptions?.map((o) => ({
      formQuestionOptionId: o.formQuestionOptionId,
      name: o.name,
    })) || []
  );

  const updateOptionName = (index: number, value: string) => {
    setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, name: value } : o)));
  };
  const addOption = () => setOptions((prev) => [...prev, { name: '' }]);
  const removeOption = (index: number) =>
    setOptions((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = (values: any) => {
    const isOptionType = optionTypeIds.has(Number(values.formQuestionTypeId));
    const isNumericType = numericTypeIds.has(Number(values.formQuestionTypeId));
    onSave({
      ...values,
      isRequired: signatureTypeIds.has(Number(values.formQuestionTypeId)) || values.isRequired || false,
      // For option-based types send the cleaned list; for other types send an
      // empty list so any previously-configured options are cleared.
      options: isOptionType ? options.filter((o) => o.name.trim() !== '') : [],
      // Only numeric types carry decimal places; anything else is reset to 0 so
      // a question switched away from Number doesn't keep a stale setting.
      decimalPlaces: isNumericType ? clampDecimalPlaces(values.decimalPlaces) : 0,
    });
  };

  return (
    <div className="inline-form">
      <Form
        // Options are held in local state, not Kendo fields, so editing only the
        // options doesn't mark the form as "modified". Kendo suppresses onSubmit
        // for an unmodified form unless ignoreModified is set, which would make
        // an options-only change fail to save.
        ignoreModified={true}
        initialValues={{
          name: question?.name || '',
          description: question?.description || '',
          placeholder: question?.placeholder || '',
          templatePlaceholder: question?.templatePlaceholder || '',
          order: question?.order || nextOrder,
          formQuestionTypeId: question?.formQuestionTypeId || (questionTypes[0]?.value || 1),
          isRequired: question?.isRequired || false,
          minLength: question?.minLength || null,
          maxLength: question?.maxLength || null,
          decimalPlaces: question?.decimalPlaces ?? 0,
          regex: question?.regex || '',
          validationMessage: question?.validationMessage || '',
        }}
        onSubmit={handleSubmit}
        render={(formRenderProps) => (
          <FormElement>
            <Field
              name="name"
              component={FormInput}
              label="Question Name:"
              validator={requiredValidator}
            />
            <Field
              name="description"
              component={FormTextArea}
              label="Description:"
              rows={2}
            />
            <Field
              name="placeholder"
              component={FormInput}
              label="Placeholder:"
            />
            {createFromTemplate && (
              <Field
                name="templatePlaceholder"
                component={FormInput}
                label="Template Placeholder:"
              />
            )}
            <Field
              name="order"
              component={FormInput}
              label="Order:"
              type="number"
              validator={requiredValidator}
            />
            <div className="k-form-field">
              <label className="k-label">Question Type:</label>
              <Field name="formQuestionTypeId" component="select" className="k-textbox">
                {questionTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.text}
                  </option>
                ))}
              </Field>
            </div>
            {optionTypeIds.has(Number(formRenderProps.valueGetter('formQuestionTypeId'))) && (
              <div className="k-form-field">
                <label className="k-label">Options:</label>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: '#666' }}>
                  Values shown in the dropdown/choice list on the form.
                </p>
                {options.length === 0 && (
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: '#a00' }}>
                    No options added yet.
                  </p>
                )}
                {options.map((option, index) => (
                  <div
                    key={index}
                    style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}
                  >
                    <input
                      className="k-textbox"
                      style={{ flex: 1 }}
                      value={option.name}
                      placeholder={`Option ${index + 1}`}
                      onChange={(e) => updateOptionName(index, e.target.value)}
                    />
                    <Button
                      type="button"
                      fillMode="flat"
                      onClick={() => removeOption(index)}
                      title="Remove option"
                    >
                      <FaTrash />
                    </Button>
                  </div>
                ))}
                <Button type="button" fillMode="outline" onClick={addOption}>
                  <FaPlus style={{ marginRight: '0.5rem' }} />
                  Add Option
                </Button>
              </div>
            )}
            {numericTypeIds.has(Number(formRenderProps.valueGetter('formQuestionTypeId'))) && (
              <div className="k-form-field">
                <Field
                  name="decimalPlaces"
                  component={FormInput}
                  label="Decimal Places:"
                  type="number"
                  min={0}
                  max={MAX_DECIMAL_PLACES}
                />
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#666' }}>
                  Leave at 0 for whole numbers only. Higher values let the answer be
                  entered to that many decimal places (up to {MAX_DECIMAL_PLACES}).
                </p>
              </div>
            )}
            {signatureTypeIds.has(Number(formRenderProps.valueGetter('formQuestionTypeId'))) ? (
              <p style={{ margin: '0.5rem 0', fontSize: '0.875rem', color: '#555' }}>
                <strong>Required.</strong> A Signature question is always required: the person filling in the form
                signs on their phone, and the signature is saved as an image with their response.
              </p>
            ) : (
              <Field
                name="isRequired"
                component={FormCheckbox}
                label="Required"
              />
            )}
            <Field
              name="minLength"
              component={FormInput}
              label="Min Length:"
              type="number"
            />
            <Field
              name="maxLength"
              component={FormInput}
              label="Max Length:"
              type="number"
            />
            <Field
              name="regex"
              component={FormInput}
              label="Regex Pattern:"
            />
            <Field
              name="validationMessage"
              component={FormInput}
              label="Validation Message:"
            />
            <div className="form-actions">
              <Button type="submit" themeColor="primary">
                <FaSave style={{ marginRight: '0.5rem' }} />
                Save
              </Button>
              <Button onClick={onCancel} fillMode="outline">
                <FaTimes style={{ marginRight: '0.5rem' }} />
                Cancel
              </Button>
            </div>
          </FormElement>
        )}
      />
    </div>
  );
};

export default EditFormDetails;
